import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useProductsWithBestPromotion } from "@/hooks/use-menu";
import { formatPrice } from "@/lib/format-price";
import { useOrgCurrency } from "@/hooks/use-org-settings";
import { useActiveOrganization } from "@/hooks/use-organizations";

export default function ProductsViewPage() {
  const { t } = useTranslation();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const currency = useOrgCurrency(activeOrganization?.id);
  const { data, error, isLoading } = useProductsWithBestPromotion(activeOrganization?.id);

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.productsView.title")}</CardTitle>
            <CardDescription>
              {t("menu.productsView.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  let productsContent: ReactNode = (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <h2 className="font-medium text-base">{t("menu.productsView.table.emptyTitle")}</h2>
      <p className="mt-2 text-muted-foreground text-sm">
        {t("menu.productsView.table.emptyDescription")}
      </p>
    </div>
  );

  if (isLoading) {
    productsContent = (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (error) {
    productsContent = <p className="text-destructive text-sm">{error.message}</p>;
  } else if (data?.products.length) {
    productsContent = (
      <div className="space-y-4">
        {data.warnings.length > 0 ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-amber-700 text-sm dark:text-amber-300">
            {t("menu.productsView.tieWarning", {
              count: data.warnings.length,
              defaultValue:
                "Hay {{count}} productos con promociones empatadas. Se aplica la promoción más nueva.",
            })}
          </div>
        ) : null}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("menu.productsView.table.productName")}</TableHead>
              <TableHead>{t("menu.productsView.table.sectionName")}</TableHead>
              <TableHead>{t("menu.productsView.table.collectionName")}</TableHead>
              <TableHead>{t("menu.productsView.table.originalPrice")}</TableHead>
              <TableHead>{t("menu.productsView.table.discount")}</TableHead>
              <TableHead>{t("menu.productsView.table.promotionName")}</TableHead>
              <TableHead>{t("menu.productsView.table.discountedPrice")}</TableHead>
              <TableHead>{t("menu.productsView.table.promotionType")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <p className="font-medium">{product.name}</p>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {product.sectionId ?? "-"}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {product.collectionId ?? "-"}
                </TableCell>
                <TableCell className="text-sm">
                  {formatPrice(product.originalPrice, currency)}
                </TableCell>
                <TableCell className="text-sm">
                  {product.discountPercentage !== null
                    ? `${product.discountPercentage}%${
                        product.promotionMinQuantity
                          ? ` desde ${product.promotionMinQuantity} uds.`
                          : ""
                      }`
                    : "-"}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {product.promotionName ?? "-"}
                </TableCell>
                <TableCell className="text-sm">
                  {product.discountedPrice !== null
                    ? formatPrice(product.discountedPrice, currency)
                    : "-"}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {product.promotionType ?? "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="space-y-1">
        <h1 className="font-serif font-semibold text-2xl tracking-tight">
          {t("menu.productsView.title")}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t("menu.productsView.description", {
            organization: activeOrganization.name,
          })}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{t("menu.productsView.table.title")}</CardTitle>
          <CardDescription>{t("menu.productsView.table.description")}</CardDescription>
        </CardHeader>
        <CardContent>{productsContent}</CardContent>
      </Card>
    </div>
  );
}
